/**
 * UserManagementCommandCenter — Iter 71.4.
 *
 * Platform-admin only. Cross-tenant user directory with filters,
 * password reset issuance, bulk invite, ownership transfer, session
 * management, and login history. Mounted at /platform/users and also
 * re-used as the Users tab inside the Tenant Command Center.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, Search, Loader2, RefreshCw, KeyRound, UserPlus,
  ShieldAlert, Crown, Power, History, Monitor, X,
  CheckCircle2, AlertTriangle, ChevronDown,
} from "lucide-react";

const ROLE_OPTIONS = [
  { value: "", label: "All Roles" },
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "project_manager", label: "Project Manager" },
  { value: "superintendent", label: "Superintendent" },
  { value: "field_engineer", label: "Field Engineer" },
  { value: "safety_manager", label: "Safety Manager" },
  { value: "inspector", label: "Inspector / QC" },
  { value: "foreman", label: "Foreman" },
  { value: "subcontractor", label: "Subcontractor" },
  { value: "viewer", label: "Viewer" },
  { value: "executive", label: "Executive" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "disabled", label: "Disabled" },
];

const STATUS_COLOR = {
  active: "#86efac", pending: "#fbbf24", disabled: "#fca5a5",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined,
    { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
};


function CopyBtn({ value, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => {
              navigator.clipboard.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 1200);
            }}
            className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded border hover:bg-[var(--fop-surface-2)]"
            style={{ borderColor: "var(--fop-border)" }}
            data-testid={testid}>
      {copied ? "Copied" : "Copy"}
    </button>
  );
}


function PasswordResetDialog({ user, onClose }) {
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);
  const issue = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/platform/users/${user.id}/password-reset`);
      setIssued(r.data);
      toast.success("Password reset link issued");
    } catch (e) {
      toast.error("Failed to issue password reset");
    } finally { setBusy(false); }
  };
  const fullUrl = issued ? `${window.location.origin}${issued.url}` : "";
  return (
    <ModalShell onClose={onClose} title="Issue Password Reset" testid="pwreset-dialog">
      <p className="text-sm text-[var(--fop-text-2)]">
        Issue a single-use password reset link for{" "}
        <span className="font-medium text-[var(--fop-text)]">{user.email}</span>.
        The link expires in 24 hours.
      </p>
      {!issued ? (
        <button onClick={issue} disabled={busy}
                className="h-10 px-4 rounded-md bg-[var(--fop-cta-bg)] text-white text-sm font-medium hover:bg-[var(--fop-cta-bg-hover)] disabled:opacity-50"
                data-testid="pwreset-issue-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Issue reset link"}
        </button>
      ) : (
        <div className="space-y-2">
          <div className="text-xs font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
            Reset link (expires {fmtDate(issued.expires_at)})
          </div>
          <div className="flex items-center gap-2">
            <input readOnly value={fullUrl}
                     className="flex-1 h-10 px-3 rounded-md border bg-[var(--fop-surface-1)] text-xs font-mono text-[var(--fop-text)]"
                     style={{ borderColor: "var(--fop-border)" }}
                     data-testid="pwreset-link-input" />
            <CopyBtn value={fullUrl} testid="pwreset-copy-btn" />
          </div>
        </div>
      )}
    </ModalShell>
  );
}


function BulkInviteDialog({ tenantId, onClose, onCreated }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parseRows = () => {
    return text.split("\n").map((line) => line.trim()).filter(Boolean)
      .map((line) => {
        const parts = line.split(",").map((s) => s.trim());
        return {
          email: parts[0] || "",
          name:  parts[1] || "",
          role:  parts[2] || "viewer",
        };
      });
  };

  const submit = async () => {
    const rows = parseRows();
    if (rows.length === 0) { toast.error("Paste at least one row"); return; }
    if (rows.length > 250) { toast.error("Maximum 250 rows per submission"); return; }
    setBusy(true);
    try {
      const r = await api.post(
        `/platform/tenants/${tenantId}/users/bulk-invite`,
        { rows });
      setResult(r.data);
      if (r.data.ok) {
        toast.success(`${r.data.created_count} invitations created`);
        onCreated && onCreated();
      } else {
        toast.error(`Validation failed (${r.data.errors.length} errors)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk invite failed");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} title="Bulk Invite Users"
                  testid="bulk-invite-dialog" wide>
      <p className="text-xs text-[var(--fop-text-3)]">
        Paste one user per line in CSV format: <span className="font-mono">email,name,role</span>.
        Maximum 250 rows. All rows are validated before any invitation is created. Owner/Admin roles
        cannot be assigned via bulk — use the explicit Transfer Ownership flow.
      </p>
      <textarea value={text} onChange={(e) => setText(e.target.value)}
                  rows={10} placeholder={"jane@company.com,Jane Doe,project_manager\nfoo@company.com,Foo Bar,viewer"}
                  className="w-full p-3 rounded-md border bg-[var(--fop-surface-1)] text-xs font-mono text-[var(--fop-text)] focus:border-[var(--fop-primary)] outline-none"
                  style={{ borderColor: "var(--fop-border)" }}
                  data-testid="bulk-invite-textarea" />
      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          {parseRows().length} row(s)
        </div>
        <button onClick={submit} disabled={busy || parseRows().length === 0}
                className="h-10 px-4 rounded-md bg-[var(--fop-cta-bg)] text-white text-sm font-medium hover:bg-[var(--fop-cta-bg-hover)] disabled:opacity-50"
                data-testid="bulk-invite-submit-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Validate & Create"}
        </button>
      </div>
      {result && (
        <div className="rounded-md border p-3 space-y-2"
             style={{ borderColor: "var(--fop-border)", background: "var(--fop-surface-1)" }}
             data-testid="bulk-invite-result">
          <div className="text-sm text-[var(--fop-text)]">
            Created: <span className="font-mono">{result.created_count}</span> ·
            Errors: <span className="font-mono">{result.errors?.length || 0}</span>
          </div>
          {result.errors?.length > 0 && (
            <div className="max-h-40 overflow-y-auto space-y-1">
              {result.errors.map((e, i) => (
                <div key={i} className="text-[11px] font-mono text-[var(--fop-text-2)]">
                  Row {e.row + 1}: <span className="text-[#fca5a5]">{e.code}</span>
                  {e.email && <> · {e.email}</>}
                  {e.detail && <> · {e.detail.slice(0, 80)}</>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </ModalShell>
  );
}


function TransferOwnershipDialog({ tenantId, users, currentOwner, onClose, onDone }) {
  const eligible = users.filter((u) =>
    u.id !== currentOwner?.id && u.status !== "disabled");
  const [targetId, setTargetId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!targetId) { toast.error("Pick a target user"); return; }
    setBusy(true);
    try {
      await api.post(`/platform/tenants/${tenantId}/users/transfer-ownership`, {
        from_user_id: currentOwner?.id || null,
        to_user_id: targetId,
        reason: reason || null,
      });
      toast.success("Ownership transferred");
      onDone && onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed");
    } finally { setBusy(false); }
  };
  return (
    <ModalShell onClose={onClose} title="Transfer Tenant Ownership"
                  testid="transfer-ownership-dialog">
      <p className="text-xs text-[var(--fop-text-3)]">
        Move the <span className="font-mono">owner</span> role. The current owner will be demoted to
        <span className="font-mono"> admin</span> (access preserved). The new owner must be active.
      </p>
      <div className="space-y-1">
        <label className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          Current owner
        </label>
        <div className="text-sm text-[var(--fop-text)]">
          {currentOwner ? `${currentOwner.name} · ${currentOwner.email}` : "—"}
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          New owner
        </label>
        <select value={targetId} onChange={(e) => setTargetId(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border bg-[var(--fop-surface-1)] text-sm text-[var(--fop-text)]"
                  style={{ borderColor: "var(--fop-border)" }}
                  data-testid="transfer-target-select">
          <option value="">— Select a user —</option>
          {eligible.map((u) => (
            <option key={u.id} value={u.id}>{u.name} · {u.email} ({u.role})</option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <label className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          Reason (optional)
        </label>
        <input value={reason} onChange={(e) => setReason(e.target.value)}
                 className="w-full h-10 px-3 rounded-md border bg-[var(--fop-surface-1)] text-sm text-[var(--fop-text)]"
                 style={{ borderColor: "var(--fop-border)" }}
                 data-testid="transfer-reason-input" />
      </div>
      <div className="flex items-center justify-end">
        <button onClick={submit} disabled={busy || !targetId}
                className="h-10 px-4 rounded-md bg-[var(--fop-cta-bg)] text-white text-sm font-medium hover:bg-[var(--fop-cta-bg-hover)] disabled:opacity-50"
                data-testid="transfer-submit-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Transfer ownership"}
        </button>
      </div>
    </ModalShell>
  );
}


function SessionsDialog({ user, onClose, currentActorId }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/platform/users/${user.id}/sessions`);
      setSessions(r.data.sessions || []);
    } catch (e) {
      toast.error("Failed to load sessions");
    } finally { setLoading(false); }
  }, [user.id]);
  useEffect(() => { load(); }, [load]);

  const revokeOne = async (jti) => {
    const isSelf = user.id === currentActorId;
    if (isSelf && !window.confirm("This will sign YOU out. Continue?")) return;
    try {
      await api.delete(
        `/platform/users/${user.id}/sessions/${jti}` +
        (isSelf ? "?confirm_self=true" : ""));
      toast.success("Session revoked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  const revokeAll = async () => {
    const isSelf = user.id === currentActorId;
    if (isSelf && !window.confirm("This will sign YOU out of every device. Continue?")) return;
    if (!isSelf && !window.confirm(`Revoke ALL ${sessions.length} sessions for ${user.email}?`)) return;
    try {
      await api.delete(
        `/platform/users/${user.id}/sessions` +
        (isSelf ? "?confirm_self=true" : ""));
      toast.success("All sessions revoked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  return (
    <ModalShell onClose={onClose} title={`Active Sessions · ${user.email}`}
                  testid="sessions-dialog" wide>
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-[var(--fop-text-3)] font-mono uppercase tracking-wider">
          {sessions.length} active session(s)
        </div>
        {sessions.length > 0 && (
          <button onClick={revokeAll}
                  className="h-9 px-3 rounded-md border text-xs uppercase tracking-wider font-mono text-[#fca5a5] hover:bg-[var(--fop-surface-2)]"
                  style={{ borderColor: "var(--fop-border)" }}
                  data-testid="sessions-revoke-all-btn">
            Revoke all
          </button>
        )}
      </div>
      <div className="space-y-1 max-h-72 overflow-y-auto">
        {loading ? (
          <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : sessions.length === 0 ? (
          <div className="p-6 text-center text-xs text-[var(--fop-text-3)]"
               data-testid="sessions-empty">
            No active sessions.
          </div>
        ) : sessions.map((s) => (
          <div key={s.jti}
               className="flex items-center justify-between gap-3 p-2 rounded border"
               style={{ borderColor: "var(--fop-border)", background: "var(--fop-surface-1)" }}
               data-testid={`session-row-${s.jti}`}>
            <div className="min-w-0">
              <div className="text-xs text-[var(--fop-text)] truncate">
                {s.ip || "—"}
              </div>
              <div className="text-[10px] font-mono text-[var(--fop-text-3)] truncate">
                {s.user_agent?.slice(0, 60) || "—"} · created {fmtDate(s.created_at)}
              </div>
            </div>
            <button onClick={() => revokeOne(s.jti)}
                    className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded border hover:bg-[var(--fop-surface-2)] text-[#fca5a5]"
                    style={{ borderColor: "var(--fop-border)" }}
                    data-testid={`session-revoke-${s.jti}`}>
              Revoke
            </button>
          </div>
        ))}
      </div>
    </ModalShell>
  );
}


function LoginHistoryDialog({ user, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/platform/users/${user.id}/login-history`);
        setEvents(r.data.events || []);
      } catch (e) {
        toast.error("Failed to load history");
      } finally { setLoading(false); }
    })();
  }, [user.id]);
  return (
    <ModalShell onClose={onClose} title={`Login History · ${user.email}`}
                  testid="login-history-dialog" wide>
      <div className="text-xs text-[var(--fop-text-3)] font-mono uppercase tracking-wider">
        {events.length} event(s)
      </div>
      <div className="space-y-1 max-h-80 overflow-y-auto">
        {loading ? (
          <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : events.length === 0 ? (
          <div className="p-6 text-center text-xs text-[var(--fop-text-3)]">
            No events recorded.
          </div>
        ) : events.map((e, i) => (
          <div key={i}
               className="flex items-center justify-between gap-3 px-3 py-2 rounded border"
               style={{ borderColor: "var(--fop-border)", background: "var(--fop-surface-1)" }}>
            <div className="min-w-0">
              <div className="text-xs text-[var(--fop-text)] truncate">
                <span className="font-mono">{e.action}</span>
              </div>
              <div className="text-[10px] font-mono text-[var(--fop-text-3)] truncate">
                {fmtDate(e.created_at || e.timestamp)}
                {e.ip ? <> · {e.ip}</> : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </ModalShell>
  );
}


function ModalShell({ onClose, title, testid, wide, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
         onClick={onClose} data-testid={testid}>
      <div className={`w-full ${wide ? "max-w-2xl" : "max-w-md"} rounded-xl border bg-[var(--fop-surface)] flex flex-col max-h-[90vh]`}
           style={{ borderColor: "var(--fop-border)" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between"
             style={{ borderColor: "var(--fop-border)" }}>
          <div className="font-semibold text-[var(--fop-text)]">{title}</div>
          <button onClick={onClose}
                  className="w-8 h-8 inline-flex items-center justify-center rounded hover:bg-[var(--fop-surface-2)]"
                  data-testid={`${testid}-close`}>
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}


function UserRow({ u, onAction }) {
  const status = u.status || "active";
  const isOwner = u.role === "owner";
  return (
    <div className="grid grid-cols-12 gap-3 px-4 py-3 items-center border-b last:border-b-0"
         style={{ borderColor: "var(--fop-border)" }}
         data-testid={`um-row-${u.id}`}>
      <div className="col-span-12 sm:col-span-4 min-w-0">
        <div className="text-sm font-medium text-[var(--fop-text)] truncate flex items-center gap-1.5">
          {isOwner && <Crown size={12} className="text-[var(--fop-primary)] shrink-0" />}
          {u.name || u.email}
        </div>
        <div className="text-xs text-[var(--fop-text-3)] font-mono truncate">{u.email}</div>
      </div>
      <div className="col-span-6 sm:col-span-2 min-w-0">
        <Link to={`/platform/tenants/${u.tenant?.id}`}
              className="text-xs text-[var(--fop-text-2)] hover:text-[var(--fop-primary)] truncate block">
          {u.tenant?.display_name || u.tenant?.slug || u.tenant_id}
        </Link>
        <div className="text-[10px] font-mono text-[var(--fop-text-3)] truncate uppercase">
          {u.tenant?.lifecycle_stage || "—"}
        </div>
      </div>
      <div className="col-span-3 sm:col-span-1 text-xs font-mono text-[var(--fop-text-2)] uppercase tracking-wider">
        {u.role}
      </div>
      <div className="col-span-3 sm:col-span-1">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider"
              style={{ color: STATUS_COLOR[status] || "#cbd5e1" }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_COLOR[status] || "#cbd5e1" }} />
          {status}
        </span>
        {u.pending_invitation && (
          <div className="text-[10px] font-mono text-[var(--fop-text-3)] mt-0.5">
            invite pending
          </div>
        )}
      </div>
      <div className="col-span-6 sm:col-span-2 text-xs text-[var(--fop-text-2)] tabular-nums">
        {fmtDate(u.last_login_at)}
        <div className="text-[10px] font-mono text-[var(--fop-text-3)]">
          last login
        </div>
      </div>
      <div className="col-span-6 sm:col-span-2 flex items-center gap-1 justify-end">
        <ActionMenu u={u} onAction={onAction} />
      </div>
    </div>
  );
}


function ActionMenu({ u, onAction }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onDoc = () => setOpen(false);
    window.addEventListener("click", onDoc);
    return () => window.removeEventListener("click", onDoc);
  }, [open]);
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setOpen((v) => !v)}
              className="h-8 px-2 rounded-md border text-[11px] font-mono uppercase tracking-wider hover:bg-[var(--fop-surface-2)] inline-flex items-center gap-1"
              style={{ borderColor: "var(--fop-border)" }}
              data-testid={`um-actions-${u.id}`}>
        Actions <ChevronDown size={11} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-52 rounded-md border bg-[var(--fop-surface)] shadow-xl z-30 py-1"
             style={{ borderColor: "var(--fop-border)" }}>
          <MenuBtn icon={KeyRound}  onClick={() => onAction("password_reset", u)}
                     testid={`um-action-reset-${u.id}`}>Issue password reset</MenuBtn>
          <MenuBtn icon={Monitor}   onClick={() => onAction("sessions", u)}
                     testid={`um-action-sessions-${u.id}`}>Manage sessions</MenuBtn>
          <MenuBtn icon={History}   onClick={() => onAction("history", u)}
                     testid={`um-action-history-${u.id}`}>Login history</MenuBtn>
          {u.role !== "owner" && (
            <MenuBtn icon={Crown}   onClick={() => onAction("transfer_to", u)}
                       testid={`um-action-transfer-${u.id}`}>Promote to owner</MenuBtn>
          )}
          {u.status === "active" && (
            <MenuBtn icon={Power} danger onClick={() => onAction("disable", u)}
                       testid={`um-action-disable-${u.id}`}>Disable user</MenuBtn>
          )}
          {u.status === "disabled" && (
            <MenuBtn icon={CheckCircle2} onClick={() => onAction("enable", u)}
                       testid={`um-action-enable-${u.id}`}>Enable user</MenuBtn>
          )}
        </div>
      )}
    </div>
  );
}

function MenuBtn({ icon: Icon, children, onClick, danger, testid }) {
  return (
    <button onClick={onClick}
            className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-[var(--fop-surface-2)] ${
              danger ? "text-[#fca5a5]" : "text-[var(--fop-text-2)]"
            }`}
            data-testid={testid}>
      <Icon size={13} />
      <span>{children}</span>
    </button>
  );
}


export default function UserManagementCommandCenter({
  tenantId: scopedTenantId = null, currentActorId = null, embedded = false,
}) {
  const [filters, setFilters] = useState({
    tenant_id: scopedTenantId || "", role: "", status: "", search: "",
  });
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.tenant_id) params.tenant_id = filters.tenant_id;
      if (filters.role)      params.role      = filters.role;
      if (filters.status)    params.status    = filters.status;
      if (filters.search)    params.search    = filters.search;
      const r = await api.get("/platform/users", { params });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load users");
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const users = useMemo(() => data?.users || [], [data]);
  const summary = data?.summary || {};

  const tenantOwners = useMemo(() => {
    if (!scopedTenantId) return null;
    return users.filter((u) => u.role === "owner");
  }, [users, scopedTenantId]);

  const handleAction = async (action, u) => {
    if (action === "password_reset") setDialog({ type: "pwreset", user: u });
    else if (action === "sessions")  setDialog({ type: "sessions", user: u });
    else if (action === "history")   setDialog({ type: "history",  user: u });
    else if (action === "transfer_to" && scopedTenantId) {
      setDialog({ type: "transfer", tenantId: scopedTenantId, currentOwner: tenantOwners?.[0],
                  preTarget: u });
    }
    else if (action === "transfer_to") {
      // Global view — open the transfer dialog scoped to the user's tenant.
      setDialog({ type: "transfer", tenantId: u.tenant?.id || u.tenant_id,
                  currentOwner: null, preTarget: u });
    }
    else if (action === "disable") {
      try {
        await api.patch(`/platform/users/${u.id}/status`, { status: "disabled" });
        toast.success("User disabled");
        load();
      } catch (e) {
        const detail = e?.response?.data?.detail || "Disable failed";
        toast.error(detail);
      }
    }
    else if (action === "enable") {
      try {
        await api.patch(`/platform/users/${u.id}/status`, { status: "active" });
        toast.success("User enabled");
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Enable failed");
      }
    }
  };

  const Container = embedded ? "div" : "div";
  return (
    <Container className={embedded
                    ? "rounded-xl border bg-[var(--fop-surface)]"
                    : "p-4 sm:p-6 space-y-6 max-w-7xl mx-auto"}
                 style={embedded ? { borderColor: "var(--fop-border)" } : {}}
                 data-testid="user-management-cc">

      {/* Header */}
      <div className={embedded
                          ? "px-5 py-4 border-b flex items-center justify-between gap-3 flex-wrap"
                          : "flex items-center justify-between gap-3 flex-wrap"}
            style={embedded ? { borderColor: "var(--fop-border)" } : {}}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md inline-flex items-center justify-center"
               style={{ background: "color-mix(in srgb, var(--fop-primary) 14%, transparent)" }}>
            <Users size={16} className="text-[var(--fop-primary)]" />
          </div>
          <div>
            <div className="text-[var(--fop-text)] font-semibold">
              {scopedTenantId ? "Users" : "User Management Command Center"}
            </div>
            <div className="text-xs text-[var(--fop-text-3)] font-mono uppercase tracking-wider">
              {summary.total ?? 0} users · {summary.by_status?.active ?? 0} active ·
              {" "}{summary.by_status?.pending ?? 0} pending ·
              {" "}{summary.by_status?.disabled ?? 0} disabled
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {scopedTenantId && (
            <button onClick={() => setDialog({ type: "bulk", tenantId: scopedTenantId })}
                    className="h-9 px-3 rounded-md bg-[var(--fop-cta-bg)] text-white text-xs font-mono uppercase tracking-wider hover:bg-[var(--fop-cta-bg-hover)] inline-flex items-center gap-1.5"
                    data-testid="um-bulk-invite-btn">
              <UserPlus size={13} /> Bulk Invite
            </button>
          )}
          {scopedTenantId && tenantOwners?.[0] && (
            <button onClick={() => setDialog({ type: "transfer", tenantId: scopedTenantId,
                                               currentOwner: tenantOwners[0] })}
                    className="h-9 px-3 rounded-md border text-xs font-mono uppercase tracking-wider hover:bg-[var(--fop-surface-2)] inline-flex items-center gap-1.5"
                    style={{ borderColor: "var(--fop-border)" }}
                    data-testid="um-transfer-ownership-btn">
              <Crown size={13} /> Transfer ownership
            </button>
          )}
          <button onClick={load} disabled={loading}
                  className="h-9 px-3 rounded-md border text-xs font-mono uppercase tracking-wider hover:bg-[var(--fop-surface-2)] inline-flex items-center gap-1.5"
                  style={{ borderColor: "var(--fop-border)" }}
                  data-testid="um-refresh-btn">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className={embedded
                          ? "px-5 py-3 border-b flex items-center gap-2 flex-wrap"
                          : "flex items-center gap-2 flex-wrap"}
            style={embedded ? { borderColor: "var(--fop-border)" } : {}}>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fop-text-3)]" />
          <input value={filters.search}
                   onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                   placeholder="Search email or name…"
                   className="w-full h-9 pl-9 pr-3 rounded-md border bg-[var(--fop-surface-1)] text-sm focus:border-[var(--fop-primary)] outline-none"
                   style={{ borderColor: "var(--fop-border)" }}
                   data-testid="um-search-input" />
        </div>
        <select value={filters.role}
                  onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value }))}
                  className="h-9 px-2 rounded-md border bg-[var(--fop-surface-1)] text-xs uppercase tracking-wider font-mono"
                  style={{ borderColor: "var(--fop-border)" }}
                  data-testid="um-role-filter">
          {ROLE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select value={filters.status}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  className="h-9 px-2 rounded-md border bg-[var(--fop-surface-1)] text-xs uppercase tracking-wider font-mono"
                  style={{ borderColor: "var(--fop-border)" }}
                  data-testid="um-status-filter">
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* List */}
      <div className={embedded ? "" : "rounded-xl border bg-[var(--fop-surface)] overflow-hidden"}
            style={embedded ? {} : { borderColor: "var(--fop-border)" }}>
        <div className="hidden sm:grid grid-cols-12 gap-3 px-4 py-2 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]"
              style={{ borderBottom: "1px solid var(--fop-border)" }}>
          <div className="col-span-4">User</div>
          <div className="col-span-2">Tenant</div>
          <div className="col-span-1">Role</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2">Last login</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-[var(--fop-text-3)] text-sm"
               data-testid="um-empty">
            No users match the current filters.
          </div>
        ) : (
          users.map((u) => <UserRow key={u.id} u={u} onAction={handleAction} />)
        )}
      </div>

      {dialog?.type === "pwreset" && (
        <PasswordResetDialog user={dialog.user} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "sessions" && (
        <SessionsDialog user={dialog.user} onClose={() => setDialog(null)}
                         currentActorId={currentActorId} />
      )}
      {dialog?.type === "history" && (
        <LoginHistoryDialog user={dialog.user} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "bulk" && (
        <BulkInviteDialog tenantId={dialog.tenantId}
                            onClose={() => setDialog(null)}
                            onCreated={load} />
      )}
      {dialog?.type === "transfer" && (
        <TransferOwnershipDialog tenantId={dialog.tenantId}
                                    users={users}
                                    currentOwner={dialog.currentOwner}
                                    onClose={() => setDialog(null)}
                                    onDone={load} />
      )}
    </Container>
  );
}
